import type { Dictionary } from '@/i18n/dictionary'
import { mediusTermsEn } from '@/i18n/medius-terms'
import { mediusDeletionEn, mediusPrivacyEn } from '@/i18n/medius-policies'
import { sparsaAcceptableUseEn, sparsaTermsEn } from '@/i18n/sparsa-terms'
import { sparsaDeletionEn, sparsaPrivacyEn } from '@/i18n/sparsa-policies'

export const sr: Dictionary = {
  meta: {
    home: {
      title: 'Dalmos Systems — Poslovni softver po meri, CRM i integracije',
      description:
        'Dalmos Systems razvija poslovni softver po meri za finansije, nekretnine, ugostiteljstvo i logistiku — CRM, ERP i PMS integracije, mobilne aplikacije i cloud infrastrukturu, uz dugoročno održavanje.',
      keywords: [
        'poslovni softver po meri',
        'CRM implementacija',
        'softver za nekretnine',
        'softver za hotele i ugostiteljstvo',
        'ERP i PMS integracije',
        'API integracije',
        'Microsoft Dynamics 365',
        'Power Platform',
        'razvoj softvera',
      ],
    },
    privacy: {
      title: 'Politika privatnosti — Dalmos Systems',
      description:
        'Kako Dalmos Systems obrađuje lične podatke prikupljene putem kontakt forme, u skladu sa GDPR-om.',
    },
    legal: {
      title: 'Impresum — Dalmos Systems',
      description:
        'Pravni podaci o Dalmos Systems: registrovani naziv, sedište u Šibeniku, upis u Obrtni registar i kontakt.',
    },
    mediusPrivacy: {
      title: 'Medius – pravila privatnosti (engleski) — Dalmos Systems',
      description:
        'Kako Medius, usluga Dalmos Systems, obrađuje lične podatke u ime svojih korisnika. Pravila su dostupna na engleskom i hrvatskom jeziku.',
    },
    mediusDeletion: {
      title: 'Medius – brisanje podataka (engleski) — Dalmos Systems',
      description:
        'Kako zatražiti brisanje ličnih podataka iz usluge Medius, uključujući poruke primljene putem Facebook Messengera, Instagrama i WhatsAppa.',
    },
    mediusTerms: {
      title: 'Medius – uslovi korišćenja (engleski) — Dalmos Systems',
      description:
        'Uslovi pod kojima Dalmos Systems pruža Medius poslovnim korisnicima. Uslovi su dostupni na engleskom i hrvatskom jeziku.',
    },
    sparsaPrivacy: {
      title: 'Sparsa – pravila privatnosti (engleski) — Dalmos Systems',
      description:
        'Kako Sparsa, usluga Dalmos Systems, obrađuje lične podatke primalaca biltena. Pravila su dostupna na engleskom i hrvatskom jeziku.',
    },
    sparsaDeletion: {
      title: 'Sparsa – brisanje podataka (engleski) — Dalmos Systems',
      description:
        'Kako se odjaviti sa biltena poslatih putem usluge Sparsa i kako zatražiti brisanje ličnih podataka.',
    },
    sparsaTerms: {
      title: 'Sparsa – uslovi korišćenja (engleski) — Dalmos Systems',
      description:
        'Uslovi pod kojima Dalmos Systems pruža Sparsu, uslugu za slanje biltena, poslovnim korisnicima. Uslovi su dostupni na engleskom i hrvatskom jeziku.',
    },
    sparsaAcceptableUse: {
      title: 'Sparsa – prihvatljivo korišćenje (engleski) — Dalmos Systems',
      description:
        'Pravila slanja putem usluge Sparsa: saglasnost svakog primaoca, bez kupljenih spiskova, izjava pri uvozu i prijava zloupotrebe.',
    },
    notFound: {
      title: 'Stranica nije pronađena — Dalmos Systems',
      description: 'Traženu stranicu nismo našli.',
    },
  },
  nav: {
    home: 'Početna',
    services: 'Usluge',
    about: 'O nama',
    contact: 'Kontakt',
    cta: 'Zatražite ponudu',
    openMenu: 'Otvorite navigaciju',
    closeMenu: 'Zatvorite navigaciju',
    languageLabel: 'Izaberite jezik',
    currentLanguage: 'Trenutni jezik',
  },
  a11y: {
    skipToContent: 'Pređite na sadržaj',
    decorative: 'Dekorativni element',
  },
  hero: {
    eyebrow: 'Softverska rešenja po meri za poslovanje',
    headlineBefore: 'Sistemi koji rade za vaš posao,',
    headlineAccent: 'ne obrnuto.',
    subtitle:
      'Tehnički partner za poslovni softver po meri — rešenja za finansije, nekretnine, ugostiteljstvo i logistiku, izgrađena na iskustvu sa velikih CRM projekata, uz Microsoft ekosistem kao pouzdan temelj, a ne jedini fokus.',
    ctaPrimary: 'Zatražite ponudu',
    ctaSecondary: 'Pogledajte usluge',
    chips: [
      'CRM i ERP integracije',
      'Ugostiteljstvo i PMS',
      'Finansijska rešenja',
      'Nekretnine',
      'Mobilne aplikacije',
      'Warehouse i facility',
    ],
    scrollHint: 'Skrolujte',
  },
  stats: {
    title: 'Na čemu gradimo',
    items: [
      { value: '20+', label: 'Oblasti usluga pod jednim krovom' },
      { value: '6', label: 'Industrija sa proverenim obrascima rešenja' },
      { value: '1', label: 'Osoba koja vodi projekat i piše kod' },
      { value: '100%', label: 'Projekata ide u rad sa definisanom podrškom' },
    ],
  },
  why: {
    title: 'Zašto Dalmos Systems',
    cards: [
      {
        title: 'Direktan pristup',
        body: 'Bez slojeva agencije — razgovarate direktno sa osobom koja projekat i gradi.',
      },
      {
        title: 'Rešenja prilagođena industriji',
        body: 'Od finansija i nekretnina do ugostiteljstva i logistike — svaki sistem gradi se oko vaših stvarnih procesa.',
      },
      {
        title: 'Partner, a ne izvođač',
        body: 'Ostajemo uz sistem nakon isporuke — održavanje, nadogradnje i podrška deo su dogovora.',
      },
    ],
  },
  services: {
    title: 'Širok opseg rešenja, jedan izvor',
    lede: 'Od finansija i nekretnina do ugostiteljstva i infrastrukture u oblaku — svaka usluga dolazi sa implementacijom i dugoročnim održavanjem, bez prevelikog fokusa na jednu usku oblast.',
    items: {
      crm: {
        title: 'CRM i prilagođavanja',
        body: 'Uvođenje i prilagođavanje CRM sistema vašim procesima, timovima i klijentima.',
      },
      erp: {
        title: 'ERP sistemi i integracije',
        body: 'Povezivanje ERP-a sa prodajom, magacinom i finansijama i prilagođavanje postojećih modula.',
      },
      processes: {
        title: 'Procesi i optimizacija poslovanja',
        body: 'Automatizacija internih tokova rada i aplikacije koje ubrzavaju odlučivanje i smanjuju ručni rad.',
      },
      finance: {
        title: 'Finansijska rešenja',
        body: 'Sistemi za praćenje troškova, naplate i finansijsko izveštavanje.',
      },
      realEstate: {
        title: 'Nekretnine i upravljanje imovinom',
        body: 'Vođenje portfolija nekretnina, ugovora o zakupu i procena.',
      },
      mobile: {
        title: 'Mobilne aplikacije',
        body: 'Mobilna proširenja poslovnih sistema za terenski rad, magacin ili prodaju.',
      },
      hospitality: {
        title: 'Rešenja za ugostiteljstvo',
        body: 'Sistemi za hotele, restorane i smeštajne objekte — PMS integracije, rezervacije, podaci o gostima i operativa.',
      },
      web: {
        title: 'Veb rešenja i portali',
        body: 'Veb sajtovi usklađeni sa vašim poslovnim sistemom.',
      },
      reservations: {
        title: 'Rezervacioni sistemi',
        body: 'Online rezervacije u realnom vremenu, povezane sa PMS-om i naplatom.',
      },
      analytics: {
        title: 'Statistika i izveštaji',
        body: 'Dashboardi za odluke zasnovane na stvarnim podacima.',
      },
      loyalty: {
        title: 'Loyalty sistemi',
        body: 'Programi lojalnosti sa bodovima, nivoima i nagradama, članskim karticama i mobilnom aplikacijom ili karticom u digitalnom novčaniku. Ponude i kampanje prate istoriju kupovine svakog člana, a sve ostaje povezano sa vašim CRM-om.',
      },
      marketing: {
        title: 'Marketing i bilteni',
        body: 'Bilteni i kampanje ka kontaktima iz vašeg CRM-a, sa ugrađenim saglasnostima i odjavama.',
      },
      communication: {
        title: 'Komunikaciona rešenja',
        body: 'Rešenja po meri za komunikaciju vašeg tima sa klijentima, povezana sa vašim CRM-om ili samostalna.',
      },
      warehouse: {
        title: 'Upravljanje magacinom',
        body: 'Praćenje zaliha i kretanja robe.',
      },
      facility: {
        title: 'Upravljanje objektima',
        body: 'Upravljanje objektima i održavanjem.',
      },
      api: {
        title: 'API integracije i middleware',
        body: 'Povezivanje CRM-a, ERP-a, PMS-a i drugih sistema koji međusobno ne razgovaraju.',
      },
      cloud: {
        title: 'Cloud infrastruktura i DevOps',
        body: 'Azure okruženja, CI/CD i automatizacija.',
      },
      microsoft: {
        title: 'Microsoft ekosistem',
        body: 'Dynamics 365, Power Platform i Azure, kada su pravi izbor za vaše poslovanje.',
      },
      security: {
        title: 'IT bezbednost i usklađenost',
        body: 'Zaštita podataka i usklađenost sa GDPR-om.',
      },
      custom: {
        title: 'Custom softverska rešenja',
        body: 'Rešenja po meri za specifične potrebe.',
      },
      training: {
        title: 'Edukacija i onboarding',
        body: 'Obuka korisnika i interna dokumentacija.',
      },
      support: {
        title: 'Podrška i SLA ugovori',
        body: 'Definisani rokovi odziva i podrške.',
      },
    },
  },
  integrations: {
    title: 'Sistemi koji konačno razgovaraju',
    lede: 'CRM, ERP i PMS retko se povežu sami od sebe. Spajamo ih tako da se podaci unose jednom, a vide svuda — bez ručnog prepisivanja i Excel tabela kao međukoraka.',
    hubLabel: 'Jedan izvor podataka',
    systems: [
      {
        code: 'CRM',
        title: 'Kupci i prodaja',
        body: 'Klijenti, prilike i komunikacija na jednom mestu, sinhronizovani sa ponudama, ugovorima i naplatom.',
      },
      {
        code: 'ERP',
        title: 'Finansije i operativa',
        body: 'Fakturisanje, nabavka i zalihe povezani sa prodajom i magacinom, bez duplog unosa.',
      },
      {
        code: 'PMS',
        title: 'Ugostiteljstvo',
        body: 'Property management sistemi povezani sa rezervacijama, CRM-om i računovodstvom.',
      },
      {
        code: 'API',
        title: 'Sve ostalo',
        body: 'Računovodstvo, veb prodavnica, POS, channel manageri, BI alati i interne aplikacije.',
      },
    ],
    capabilitiesTitle: 'Šta dobijate',
    capabilities: [
      'Dvosmerna sinhronizacija podataka u realnom vremenu',
      'Migracije podataka bez zastoja u radu',
      'Middleware i API-ji po meri',
      'Nadzor, zapisi i upozorenja kada nešto zapne',
    ],
  },
  about: {
    title: 'O nama',
    statementBefore: 'Nova firma.',
    statementAccent: 'Iskustvo koje ne počinje od nule.',
    lede: 'Dalmos Systems osnovan je kao tehnički partner za poslovni softver po meri — finansije, nekretnine, ugostiteljstvo, logistika i više. Pristup je direktan nastavak rada na velikim projektima migracije i integracije CRM sistema u sektorima nekretnina i hotelijerstva, bez velike organizacije kao posrednika.',
    processTitle: 'Kako radimo',
    steps: [
      { index: '01', title: 'Otkrivanje', body: 'Razumevanje procesa pre prvog reda koda.' },
      {
        index: '02',
        title: 'Dizajn rešenja',
        body: 'Arhitektura prilagođena vašim sistemima, ne obrnuto.',
      },
      {
        index: '03',
        title: 'Implementacija',
        body: 'Iterativna isporuka uz redovne provere sa vama.',
      },
      { index: '04', title: 'Podrška', body: 'Ostajemo uz sistem i nakon lansiranja.' },
    ],
    valuesTitle: 'Šta nas vodi',
    values: [
      {
        title: 'Direktan pristup',
        body: 'Sa klijentom razgovara osoba koja i vodi projekat, bez slojeva komunikacije.',
      },
      {
        title: 'Transparentnost',
        body: 'Jasan obim, jasna cena i jasne granice projekta pre prvog reda koda.',
      },
      {
        title: 'Dugoročnost',
        body: 'Gradimo sisteme koje ćemo i sami održavati, ne projekte koje ostavljamo za sobom.',
      },
    ],
  },
  contact: {
    title: 'Razgovarajmo o vašem sistemu',
    lede: 'Javite se sa opisom projekta — odgovaramo predlogom sledećeg koraka.',
    responseNote: 'Odgovaramo u roku od {days} radna dana.',
    emailLabel: 'E-mail',
    phoneLabel: 'Telefon',
    form: {
      name: 'Ime i prezime',
      namePlaceholder: 'Ana Anić',
      email: 'E-mail',
      emailPlaceholder: 'ana@firma.rs',
      company: 'Firma',
      companyPlaceholder: 'Naziv firme (nije obavezno)',
      message: 'Poruka',
      messagePlaceholder: 'Opišite projekat, sisteme koje koristite i željeni rok.',
      consent: 'Pristajem da se moji podaci koriste za odgovor na ovaj upit.',
      submit: 'Pošaljite upit',
      submitting: 'Šaljemo…',
      successTitle: 'Upit je poslat.',
      successBody: 'Javićemo vam se na navedenu e-mail adresu.',
      errorTitle: 'Slanje nije uspelo.',
      errorBody: 'Probajte ponovo ili nam pišite direktno na e-mail.',
      fallbackHint: 'Otvorite e-mail klijent',
      errors: {
        nameRequired: 'Unesite ime i prezime.',
        emailRequired: 'Unesite e-mail adresu.',
        emailInvalid: 'E-mail adresa nije ispravna.',
        messageRequired: 'Unesite poruku.',
        messageTooShort: 'Poruka mora imati najmanje 20 znakova.',
        consentRequired: 'Potrebna je saglasnost za obradu podataka.',
      },
    },
  },
  footer: {
    tagline: 'Poslovni softver po meri za finansije, nekretnine, ugostiteljstvo i logistiku.',
    navTitle: 'Navigacija',
    legalTitle: 'Pravno',
    privacy: 'Politika privatnosti',
    legalNotice: 'Impresum',
    rights: 'Sva prava zadržana.',
    builtIn: 'Ulica Sedmog kontinenta 1, 22000 Šibenik, Hrvatska',
  },
  privacy: {
    title: 'Politika privatnosti',
    lastUpdatedLabel: 'Poslednja izmena',
    lastUpdated: '2026-10-31',
    intro:
      'Ova politika opisuje kako Dalmos Systems obrađuje lične podatke koje nam pošaljete preko ovog veb sajta.',
    sections: [
      {
        heading: 'Rukovalac podacima',
        paragraphs: [
          'Rukovalac podacima je {controller}. Za sva pitanja o obradi podataka pišite nam na {email}.',
        ],
      },
      {
        heading: 'Koje podatke prikupljamo',
        paragraphs: [
          'Putem kontakt forme prikupljamo ime i prezime, e-mail adresu, naziv firme ako ga unesete i sadržaj vaše poruke.',
          'Ne koristimo kolačiće za oglašavanje ni alate za praćenje posetilaca na drugim veb sajtovima.',
          'Posete brojimo alatom Cloudflare Web Analytics, Cloudflare skriptom bez kolačića (static.cloudflareinsights.com). Kada se stranica učita, skripta Cloudflare-u šalje anonimne podatke o pregledu i brzini stranice: adresu stranice, stranicu sa koje ste došli, vremena učitavanja i slična merenja rada stranice; Cloudflare iz samog zahteva dodaje pregledač, operativni sistem, vrstu uređaja i državu. Skripta ne postavlja kolačiće, ne koristi drugu memoriju na vašem uređaju i ne stvara identifikator posetioca. Vaša IP adresa stiže do Cloudflare-a sa zahtevom, kao kod svakog veb sajta; Cloudflare je ne koristi da bi vas prepoznao i u tu svrhu je ne čuva. Mi vidimo samo ukupne brojeve, kao što su pregledi stranica, sajtovi sa kojih posetioci dolaze, države i vrste uređaja, i koristimo ih da bismo razumeli kako se sajt koristi i održali ga brzim. Pravni osnov je naš legitimni interes za rad i poboljšanje veb sajta (član 6. stav 1. tačka (f) GDPR-a). Cloudflare te podatke za nas obrađuje kao obrađivač, prema svom ugovoru o obradi podataka sa standardnim ugovornim klauzulama EU za prenose van EU. Potpune podatke čuva 7 dana, a zatim uzorak od oko desetine, iz kog vidimo statistiku za poslednjih šest meseci. Skriptu možete blokirati podešavanjima pregledača ili alatom za blokiranje sadržaja; sajt i bez nje radi isto. Isto važi za veb sajt Medius, medius.dalmossystems.com, koji upućuje na ovu politiku.',
        ],
      },
      {
        heading: 'Svrha i pravni osnov',
        paragraphs: [
          'Podatke koristimo isključivo da odgovorimo na vaš upit i pripremimo ponudu. Pravni osnov je vaša saglasnost i naš legitimni interes za komunikaciju sa potencijalnim klijentima.',
        ],
      },
      {
        heading: 'Rok čuvanja',
        paragraphs: [
          'Upite čuvamo najduže dvadeset četiri meseca od poslednje komunikacije, nakon čega ih brišemo, osim ako je duže čuvanje potrebno zbog ugovorne ili zakonske obaveze.',
        ],
      },
      {
        heading: 'Vaša prava',
        paragraphs: [
          'Imate pravo na pristup podacima, ispravku, brisanje, ograničenje obrade, prenosivost podataka i pravo da povučete saglasnost u svakom trenutku.',
          'Pritužbu možete podneti nadzornom telu za zaštitu podataka, a za obradu u Hrvatskoj to je Agencija za zaštitu osobnih podataka (AZOP).',
        ],
      },
      {
        heading: 'Bezbednost i prenos podataka',
        paragraphs: [
          'Saobraćaj između vašeg pregledača i naših servera je šifrovan. Podatke ne prodajemo i ne delimo sa trećim stranama, osim sa pružaocima usluga koji nam pomažu u radu sajta i komunikaciji, na osnovu ugovora o obradi podataka.',
        ],
      },
    ],
    backHome: 'Nazad na početnu',
  },
  legal: {
    title: 'Impresum',
    intro:
      'Podaci o pružaocu usluga u skladu sa Zakonom o elektronskoj trgovini Republike Hrvatske.',
    nameLabel: 'Registrovani naziv',
    shortNameLabel: 'Kratki naziv',
    seatLabel: 'Sedište',
    country: 'Hrvatska',
    registerLabel: 'Registar',
    registerValue: 'Obrtni registar, Šibensko-kninska županija',
    mboLabel: 'Matični broj obrta (MBO)',
    registeredOnLabel: 'Datum upisa',
    activityLabel: 'Pretežna delatnost',
    activityValue:
      'Izrada, razvoj i održavanje računarskih programa i aplikacija po narudžbini (NKD 2025: 62.10.9)',
    contactLabel: 'Kontakt',
    backHome: 'Nazad na početnu',
  },
  mediusPrivacy: {
    ...mediusPrivacyEn,
    lastUpdatedLabel: 'Na snazi od',
    languageNote:
      'Ova pravila za Medius dostupna su na engleskom i hrvatskom jeziku; u nastavku je engleska verzija.',
    backHome: 'Nazad na početnu',
  },
  mediusDeletion: {
    ...mediusDeletionEn,
    lastUpdatedLabel: 'Na snazi od',
    languageNote:
      'Ova uputstva dostupna su na engleskom i hrvatskom jeziku; u nastavku je engleska verzija.',
    backHome: 'Nazad na početnu',
  },
  mediusTerms: {
    ...mediusTermsEn,
    lastUpdatedLabel: 'Na snazi od',
    languageNote:
      'Ovi uslovi za Medius dostupni su na engleskom i hrvatskom jeziku; u nastavku je engleska verzija.',
    backHome: 'Nazad na početnu',
  },
  mediusLinks: {
    mediusPrivacy: 'Medius – pravila privatnosti',
    mediusDeletion: 'Kako zatražiti brisanje podataka',
    mediusTerms: 'Medius – uslovi korišćenja',
  },
  sparsaPrivacy: {
    ...sparsaPrivacyEn,
    lastUpdatedLabel: 'Na snazi od',
    languageNote:
      'Ova pravila za Sparsu dostupna su na engleskom i hrvatskom jeziku; u nastavku je engleska verzija.',
    backHome: 'Nazad na početnu',
  },
  sparsaDeletion: {
    ...sparsaDeletionEn,
    lastUpdatedLabel: 'Na snazi od',
    languageNote:
      'Ova uputstva dostupna su na engleskom i hrvatskom jeziku; u nastavku je engleska verzija.',
    backHome: 'Nazad na početnu',
  },
  sparsaTerms: {
    ...sparsaTermsEn,
    lastUpdatedLabel: 'Na snazi od',
    languageNote:
      'Ovi uslovi za Sparsu dostupni su na engleskom i hrvatskom jeziku; u nastavku je engleska verzija.',
    backHome: 'Nazad na početnu',
  },
  sparsaAcceptableUse: {
    ...sparsaAcceptableUseEn,
    lastUpdatedLabel: 'Na snazi od',
    languageNote:
      'Ova pravila prihvatljivog korišćenja dostupna su na engleskom i hrvatskom jeziku; u nastavku je engleska verzija.',
    backHome: 'Nazad na početnu',
  },
  sparsaLinks: {
    sparsaPrivacy: 'Sparsa – pravila privatnosti',
    sparsaDeletion: 'Kako se odjaviti i izbrisati podatke',
    sparsaTerms: 'Sparsa – uslovi korišćenja',
    sparsaAcceptableUse: 'Sparsa – prihvatljivo korišćenje',
  },
  notFound: {
    code: '404',
    title: 'Stranica nije pronađena',
    body: 'Adresa koju ste otvorili ne postoji ili je premeštena.',
    cta: 'Nazad na početnu',
  },
}
